// ============================================================================
// project-switch-after-save.ts: the fail-closed save before File Explorer opens
// an imported job (App.tsx generateQuoteForPreprocessedProject).
// ----------------------------------------------------------------------------
// Moved from express-quote-guards.ts when the express Generate-quote run went
// with the old pipeline (docs/old-pipeline-removal/PLAN.md). The names and the
// error code are unchanged: App reads EXPRESS_PROJECT_SWITCH_SAVE_FAILED.
// ============================================================================

type MaybeAsyncOperation<T = unknown> = () => T | Promise<T>;

function expressProjectSaveBlocked(cause?: unknown) {
    const detail = cause instanceof Error && cause.message
        ? ` ${cause.message}`
        : '';
    const error = new Error(
        `QuoteMaker could not save the current project, so the project switch was cancelled.${detail}`,
        cause ? { cause } : undefined,
    ) as Error & {
        code: string;
        retryable: boolean;
        operatorAction: string;
    };
    error.code = 'EXPRESS_PROJECT_SWITCH_SAVE_FAILED';
    error.retryable = true;
    error.operatorAction = 'Resolve the save error, confirm the current project is saved, then start the imported quote again.';
    return error;
}

/**
 * Save the currently edited project before switching to a different project. A
 * missing or rejected save operation is a hard boundary: loadProject is not
 * called, leaving the current React/Dexie state in place.
 */
export async function switchExpressProjectAfterSaving<T>({
    hasUnsavedChanges,
    saveCurrentProject,
    loadProject,
}: {
    hasUnsavedChanges: boolean;
    saveCurrentProject?: MaybeAsyncOperation | null;
    loadProject: MaybeAsyncOperation<T>;
}): Promise<T> {
    if (typeof loadProject !== 'function') {
        throw new TypeError('switchExpressProjectAfterSaving: loadProject is required');
    }
    if (hasUnsavedChanges) {
        if (typeof saveCurrentProject !== 'function') {
            throw expressProjectSaveBlocked();
        }
        try {
            await saveCurrentProject();
        } catch (cause) {
            throw expressProjectSaveBlocked(cause);
        }
    }
    return loadProject();
}
